import React from 'react'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'
import { TeamsIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { KAMBI_PENDING_HASH_ROUTE } from '@core/Areas/KambiClient/constants'
import { getEventLinkForKambiClient } from '@core/Areas/KambiClient/helpers/navigation'
import { useNewSportsOfferingFeature } from '@core/Areas/KambiSports/Offering/hooks/useNewSportsOfferingFeature'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Player/Components/Coupon/context'
import { shouldProvideLinkToEvent } from '@core/Areas/KambiSports/Player/helpers/shouldProvideLinkToEvent'
import { CountdownStatus } from '../../../Common/CountdownStatus'
import { LegStatusIcon } from '../../../Common/LegStatusIcon'

export const Single: React.FC<{
  event: BetLoopKambiShareCoupon['events'][0]
  outcome: BetLoopKambiShareCoupon['outcomes'][0]
  betOffers: BetLoopKambiShareCoupon['betOffers']
  /** Expects raw Kambi currency value (1000 value) */
  playedOdds: BetLoopKambiShareCoupon['couponRows'][0]['playedOdds']
  betStatus:
    | BetLoopKambiShareCoupon['bets'][0]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
  isWithinMultiBet?: boolean
  /** Used for quick share of single bet to Bet Loop */
  betRef?: BetLoopKambiShareCoupon['bets'][0]['betRef']
  onMultiLegCountdownReachingZero?(): void
}> = ({
  event,
  outcome,
  betOffers,
  playedOdds,
  betStatus,
  isWithinMultiBet,
  betRef,
  onMultiLegCountdownReachingZero,
}) => {
  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)
  const isNewSportsUiEnabled = useNewSportsOfferingFeature()

  const isBetLoopQuickShare = couponDisplayContext.type === 'betloop-quick-share'
  const isBetHistoryCoupon = couponDisplayContext.type === 'bet-history'

  const shouldDisplayQuickShareToggle = !isWithinMultiBet && isBetLoopQuickShare && betRef

  const { eventName, eventStartDate, eventId, sport } = event
  const heading = outcome.label
  const subHeading = betOffers[0].criterion
  const betOfferId = outcome.betOfferId
  const outcomeId = outcome.outcomeId
  const participantId = outcome.participantId

  const shouldProvideEventLink = shouldProvideLinkToEvent({
    outcomeStatus: outcome.status,
    eventStartTime: event.eventStartDate,
  })

  const linkInfo = isBetHistoryCoupon
    ? { pathname: KAMBI_PENDING_HASH_ROUTE }
    : shouldProvideEventLink
      ? { pathname: getEventLinkForKambiClient(eventId, isNewSportsUiEnabled) }
      : undefined

  return (
    <>
      {couponDisplayContext.type === 'bet-history' && !isWithinMultiBet ? null : (
        <BetCard.EventDetails
          heading={eventName}
          iconComponent={<SportsIcon sport={sport} groupId={event.eventGroups[0].id} />}
          statusComponent={
            <>
              {(isWithinMultiBet && outcome.status === 'OPEN') || !isWithinMultiBet ? (
                <CountdownStatus
                  displayType={isWithinMultiBet ? 'multi-leg' : 'standard'}
                  eventStartTime={eventStartDate}
                  betStatus={betStatus}
                  onCountdownReachingZero={onMultiLegCountdownReachingZero}
                />
              ) : null}

              {shouldDisplayQuickShareToggle && (
                <RadioButton
                  size='md'
                  type='Tick'
                  id={betRef.toString()}
                  isChecked={couponDisplayContext.data.isSelected}
                />
              )}
            </>
          }
        />
      )}

      <BetCard.SelectionInfo
        // eslint-disable-next-line react/jsx-no-useless-fragment
        heading={<>{heading}</>}
        subHeading={subHeading}
        priceWin={playedOdds && formatKambiCurrencyToDollarDecimal(playedOdds)}
        linkInfo={linkInfo}
        rightAlignedComponent={isWithinMultiBet && <LegStatusIcon status={outcome.status} />}
        iconComponent={
          <TeamsIcon
            size='sm'
            participantId={participantId}
            betOfferType={betOffers[0].boTypeId}
            betOfferId={betOfferId}
            outcomeId={outcomeId}
            sportName={sport}
            eventId={eventId}
          />
        }
      />
    </>
  )
}
